import classNames from "classnames";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { MdOpenInNew, MdOutlineEventBusy } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import Button from "shared/components/Button";
import Select from "shared/components/Select";
import Status from "shared/components/Status";
import { useLazyQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import { USERS } from "utils/api/graphql/queries/users";
import { TRACER } from "utils/api/graphql/queries/tracers";
import {
  DATE_FORMAT,
  PROCESS_TAGS as PROCESS_TAGS_STATUSES,
  SCREENS,
  THEMES_PALETTE,
  USER_TYPES,
} from "utils/constants";
import { displayDate, formatParisDateTime } from "utils/helpers/date";
import {
  canOpenProject,
  resolveProjectStatus,
} from "utils/helpers/projectStatus";

const Projects = ({ projects, translate, isAssigning, onAssign, tracers }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const navigate = useNavigate();
  const [getTracer] = useLazyQuery(TRACER);
  const PROCESS_TAGS = t("PROCESS_TAGS", { returnObjects: true });

  const projectStatus = (project) => {
    const { processStatus } = resolveProjectStatus(project, tracers);
    const statusText =
      (project.expired && PROCESS_TAGS.EXPIRED) ||
      get(processStatus, "name", PROCESS_TAGS.IN_PROGRESS);
    return (
      <Status
        type="custom"
        style={{
          color: get(processStatus, "color"),
          background: get(processStatus, "background"),
        }}
        text={statusText}
      />
    );
  };

  const handleProjectClick = async (project) => {
    if (!canOpenProject(project, tracers)) return;

    const { tracer: resolvedTracer } = resolveProjectStatus(project, tracers);
    let tracer = resolvedTracer;
    if (get(tracer, "id") && !get(tracer, "flow.process.processStatuses")) {
      const { data } = await getTracer({
        variables: { where: { id: tracer.id } },
      });
      tracer = get(data, "tracer", tracer);
    }
    if (!get(tracer, "flow.process")) return;

    navigate(`${SCREENS.PRIVATE.BROKER.PROJECTS.DETAIL.path}/${project.id}`, {
      state: {
        process: get(tracer, "flow.process"),
        tracer,
      },
    });
  };

  return (
    <div className="projects__wrapper">
      {get(projects, "data", []).map((project, i) => {
        const { processStatus } = resolveProjectStatus(project, tracers);

        return (
          <div className="single-project" key={`project-${i}`}>
            <div className="single-project__detail">
              <div className="single-project__detail--header">
                <div className="left">
                  {projectStatus(project)}
                  <span className="product_detail--createdDate">
                    {translate.CREATED_AT}
                    {formatParisDateTime(project.createdDate)}
                  </span>
                </div>
                <div className="right">
                  <Status
                    type={"simple"}
                    text={get(
                      get(
                        THEMES_PALETTE,
                        project?.fields?.processTemplate,
                        "DEFAULT",
                      ),
                      "NAME",
                    )}
                    status={"info"}
                  />
                </div>
              </div>
            </div>
            <div className="single-project__author">
              <div
                className={classNames("single-project__author--effectiveDate", {
                  expired: project.expired,
                })}
              >
                <MdOutlineEventBusy
                  size={16}
                  color={project.expired ? COLORS.C_DANGER : COLORS.C_GRAY}
                />
                {displayDate({
                  date: get(project, "contract.issuanceDate"),
                  format: DATE_FORMAT,
                  defaultValue: "",
                })}
              </div>
              <div className="single-project__author--name">
                {project.author?.id ? (
                  <>
                    <label>{translate.ATTRIBUTED_TO}</label>
                    <span className="value">
                      {project.author?.firstname} {project.author?.lastname}
                    </span>
                  </>
                ) : (
                  <Select
                    type="async"
                    node={{
                      query: USERS,
                      variables: {
                        where: {
                          AND: [
                            { isActive: true },
                            {
                              OR: [
                                {
                                  meta: {
                                    type: USER_TYPES.INTERNAL_ADVISOR.type,
                                  },
                                },
                                {
                                  meta: {
                                    type: USER_TYPES.INTERNAL_SUPERVISOR.type,
                                  },
                                },
                                {
                                  meta: {
                                    type: USER_TYPES.BROKER_EMPLOYEE.type,
                                  },
                                },
                                {
                                  meta: { type: USER_TYPES.BROKER_ADMIN.type },
                                },
                                {
                                  meta: {
                                    type: USER_TYPES.BROKER_REPRESENTATIVE.type,
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      },
                      accessors: {
                        root: "users.data",
                        label: {
                          separator: " ",
                          keys: ["firstname", "lastname"],
                        },
                      },
                    }}
                    placeholder={translate.BROKER_FILTER_PLACEHOLDER}
                    onChange={(value) =>
                      onAssign({ projectId: project.id, authorId: value })
                    }
                    disabled={
                      isAssigning[project.author?.id] ||
                      [
                        PROCESS_TAGS_STATUSES.SIGNED,
                        PROCESS_TAGS_STATUSES.SIGNED_INCOMPLETE,
                        PROCESS_TAGS_STATUSES.SENT_TO_MANAGEMENT,
                      ].includes(get(processStatus, "tag"))
                    }
                    loading={isAssigning[project.author?.id]}
                  />
                )}
              </div>
            </div>
            <Button
              className="single-project__link"
              type="primary--link"
              onClick={() => handleProjectClick(project)}
              disabled={!canOpenProject(project, tracers)}
            >
              <span>Ouvrir le projet</span>
              <MdOpenInNew size={20} />
            </Button>
          </div>
        );
      })}
    </div>
  );
};
export default Projects;
